import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { RotateCcw, Calculator, Brain, Download, CheckCircle2, AlertCircle, Loader2, MinusCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getNaerasTodayJobRunsAsync } from '@/data/store/actions';
import { getNaerasTodayJobRuns } from '@/data/store/selectors';
import { TNaerasJob, TNaerasJobRun } from '@/data/types';
import { JOB_META, JOB_ORDER, formatAthensTime, jobOutcome, parseJobDate } from '@/pages/JobRuns/utils/jobs';

const JOB_ICONS: Record<TNaerasJob, React.ComponentType<{ className?: string }>> = {
  A: RotateCcw,
  B: Calculator,
  C: Brain,
  D: Download,
};

const TONE_BADGES = {
  success: {
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    icon: CheckCircle2,
  },
  warning: {
    chip: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    icon: Loader2,
  },
  danger: {
    chip: 'bg-rose-50 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
    icon: AlertCircle,
  },
  neutral: {
    chip: 'bg-slate-50 text-slate-700 border-slate-200',
    dot: 'bg-slate-400',
    icon: MinusCircle,
  },
};

export const SystemStatus: React.FC = () => {
  const dispatch = useAppDispatch();
  const todayRuns = useSelector(getNaerasTodayJobRuns);

  useEffect(() => {
    dispatch(getNaerasTodayJobRunsAsync()).unwrap().catch(() => undefined);
  }, [dispatch]);

  // Aggregate today's runs indexed by Job letter
  const runsMap: Partial<Record<TNaerasJob, TNaerasJobRun>> = {};
  todayRuns.forEach((run) => {
    if (!runsMap[run.job]) {
      runsMap[run.job] = run;
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.6 }}
      className="group"
    >
      <div className="rounded-3xl border border-slate-200/80 bg-white/95 p-6 shadow-xl shadow-slate-900/5 backdrop-blur-sm transition-all duration-300 hover:shadow-2xl">
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-extrabold text-slate-900">Κατάσταση Εργασιών NAERAS</h3>
            <p className="text-xs text-slate-500">Πραγματική κατάσταση αυτόματων Jobs (A, B, C, D)</p>
          </div>
          <Link
            to="/job-runs"
            className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 transition"
          >
            Όλες
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {/* 4 Real NAERAS Jobs List */}
        <div className="space-y-3">
          {JOB_ORDER.map((jobKey, index) => {
            const meta = JOB_META[jobKey];
            const run = runsMap[jobKey];
            const outcome = run ? jobOutcome(run) : { label: 'Δεν έχει εκτελεστεί', short: 'Αναμονή', tone: 'neutral' as const };
            const badge = TONE_BADGES[outcome.tone];
            const Icon = JOB_ICONS[jobKey];
            const StatusIcon = badge.icon;
            const parsedStarted = run ? parseJobDate(run.started_at) : null;

            return (
              <motion.div
                key={jobKey}
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.7 + index * 0.08 }}
                className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50/70 p-3 transition hover:bg-white hover:border-slate-200 hover:shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-700 border border-blue-100 font-black text-xs">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-900">
                        Εργασία {jobKey} &mdash; {meta.title}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500">
                      {parsedStarted ? `Τελευταία εκτέλεση: ${formatAthensTime(parsedStarted)}` : meta.schedule}
                    </p>
                  </div>
                </div>

                <div className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${badge.chip}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${badge.dot}`} />
                  <span>{outcome.short}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default SystemStatus;
